import React, { useState } from 'react';
import './Questionnaire.css';
import { FORM_FIELDS } from './config';
import { submitQuestionnaire, validateFormData } from './api';
import DevTools from './DevTools';
import { mockFormData } from './mockApi';

// Tooltip component for medical terms
const Tooltip = ({ text, children }) => {
  const [show, setShow] = useState(false);
  return (
    <span 
      style={{ position: 'relative', display: 'inline-block', cursor: 'help', borderBottom: '1px dotted #4299e1' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onClick={() => setShow(!show)}
    >
      {children}
      {show && (
        <span style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#2d3748',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '0.875rem',
          whiteSpace: 'nowrap',
          zIndex: 1000,
          marginBottom: '5px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}>
          {text}
          <span style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: '6px solid #2d3748'
          }} />
        </span>
      )}
    </span>
  );
};



const Questionnaire = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [hasChestPain, setHasChestPain] = useState(null);
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [htmlReport, setHtmlReport] = useState(null);
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  
  const [formData, setFormData] = useState({
    // Personal information
    fullName: '',
    age: '',
    gender: '',
    dateOfBirth: '',
    email: '',
    
    // Pain information
    painBegin: '',
    frequency: '',
    
    // Multi-select fields (arrays)
    characterPain: [],
    radiate: [],
    worsenedBy: [],
    relievedBy: [],
    
    // Single select
    smoke: '',
    riskFactors: []
  });

  const [errors, setErrors] = useState({});

  const totalSteps = 6; // Steps 1-5 for questions, step 6 for summary (plus step 0 for screening)

  const STEP_TITLES = {
    0: 'Screening',
    1: 'Personal',
    2: 'Pain',
    3: 'Worse',
    4: 'Relief',
    5: 'Risk',
    6: 'Review'
  };

  // Human readable labels for summary of missing fields
  const FIELD_LABELS = {
    fullName: 'Full Name',
    age: 'Age',
    gender: 'Gender',
    dateOfBirth: 'Date of Birth',
    email: 'Email Address',
    painBegin: 'Pain Begin Date',
    frequency: 'Pain Frequency',
    characterPain: 'Character of Pain',
    radiate: 'Radiation',
    worsenedBy: 'Worsened By',
    relievedBy: 'Relieved By',
    smoke: 'Smoking Status',
    riskFactors: 'Risk Factors'
  };

  // Determine missing fields for a given step without mutating error state
  const getStepErrors = (step) => {
    const missing = [];
    switch(step) {
      case 1:
        if (!formData.fullName?.trim()) missing.push('fullName');
        if (!formData.age || parseInt(formData.age,10) < 18 || parseInt(formData.age,10) > 120) missing.push('age');
        if (!formData.gender) missing.push('gender');
        if (!formData.dateOfBirth) missing.push('dateOfBirth');
        if (!formData.email) missing.push('email');
        break;
      case 2:
        if (!formData.painBegin) missing.push('painBegin');
        if (!formData.frequency) missing.push('frequency');
        if (!formData.characterPain || formData.characterPain.length === 0) missing.push('characterPain');
        if (!formData.radiate || formData.radiate.length === 0) missing.push('radiate');
        break;
      case 3:
        if (!formData.worsenedBy || formData.worsenedBy.length === 0) missing.push('worsenedBy');
        break;
      case 4:
        if (!formData.relievedBy || formData.relievedBy.length === 0) missing.push('relievedBy');
        break;
      case 5:
        if (!formData.smoke) missing.push('smoke');
        if (!formData.riskFactors || formData.riskFactors.length === 0) missing.push('riskFactors');
        break;
      default:
        break;
    }
    return missing;
  };

  const renderBadge = (step) => {
    if (step < 1 || step > 5) return null;
    const missing = getStepErrors(step);
    const isComplete = missing.length === 0;
    return (
      <span style={{
        marginLeft: '0.75rem',
        padding: '0.25rem 0.6rem',
        borderRadius: '12px',
        fontSize: '0.65rem',
        letterSpacing: '0.5px',
        fontWeight: 600,
        background: isComplete ? '#38a169' : '#e53e3e',
        color: 'white',
        verticalAlign: 'middle'
      }}>
        {isComplete ? 'Complete' : 'Incomplete'}
      </span>
    );
  };

  // Overall completion percentage (steps 1-5)
  const completedCount = [1,2,3,4,5].filter(s => getStepErrors(s).length === 0).length;
  const overallCompletionPct = (completedCount / 5) * 100;

  // Display-only formatting for option strings while keeping raw values intact for backend
  const formatDisplayText = (text) => {
    if (!text || typeof text !== 'string') return '';
    const key = text.toLowerCase();
    const specialMap = {
      'kidney disease (ckd)': 'Kidney disease (CKD)',
      'coronary artery bypass grafts (cabg)': 'Coronary artery bypass grafts (CABG)',
      'cardiac stents (pci)': 'Cardiac stents (PCI)',
      'glyceryl trinitrate (gtn) spray': 'Glyceryl trinitrate (GTN) spray',
      'antacids (gaviscon, milk of magnesia etc)': 'Antacids (Gaviscon, milk of magnesia etc)',
      'no cardiac risk factors': 'None of the above',
      'does not radiate': 'Does not radiate',
      'laying flat': 'Lying flat'
    };
    if (specialMap[key]) return specialMap[key];
    // General capitalization: capitalize first letter of words unless inside parentheses indicating an acronym
    return text.split(' ').map(word => {
      if (!word) return word;
      // Preserve existing all-caps (likely acronyms)
      if (word === word.toUpperCase()) return word;
      // Handle '(abc)' -> '(ABC)' for short acronyms
      if (word.startsWith('(') && word.endsWith(')')) {
        const inner = word.slice(1, -1);
        if (inner.length > 0 && inner.length <= 5 && /[a-z]/.test(inner) && inner === inner.toLowerCase()) {
          return '(' + inner.toUpperCase() + ')';
        }
        return '(' + inner.charAt(0).toUpperCase() + inner.slice(1) + ')';
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
  };

  // Load saved progress from localStorage on mount
  React.useEffect(() => {
    const savedData = localStorage.getItem('echestpain_progress');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        if (parsed.formData) setFormData(parsed.formData);
        if (parsed.currentStep !== undefined) setCurrentStep(parsed.currentStep);
        if (parsed.hasChestPain !== undefined) setHasChestPain(parsed.hasChestPain);
      } catch (e) {
        console.error('Error loading saved progress:', e);
      }
    }
  }, []);

  // Save progress to localStorage whenever data changes
  React.useEffect(() => {
    if (currentStep > 0 || hasChestPain !== null) {
      const dataToSave = {
        formData,
        currentStep,
        hasChestPain,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem('echestpain_progress', JSON.stringify(dataToSave));
    }
  }, [formData, currentStep, hasChestPain]);

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    
    // Don't calculate age if date is in the future
    if (birthDate > today) return '';
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    // Adjust if birthday hasn't occurred yet this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age.toString();
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    const updates = { [field]: value };
    
    // Auto-calculate age from date of birth and validate immediately
    if (field === 'dateOfBirth') {
      const today = new Date();
      const dob = new Date(value);
      
      // Validate date immediately
      if (dob > today) {
        setErrors(prev => ({
          ...prev,
          dateOfBirth: 'Date of birth cannot be in the future',
          age: 'Invalid date'
        }));
        updates.age = '';
      } else {
        const age = calculateAge(value);
        updates.age = age;
        
        // Check if age is valid (18-120)
        const ageNum = parseInt(age);
        if (age && ageNum < 18) {
          setErrors(prev => ({
            ...prev,
            dateOfBirth: 'You must be 18 years or older to use this service',
            age: 'Must be 18 or older'
          }));
        } else if (age && ageNum > 120) {
          setErrors(prev => ({
            ...prev,
            dateOfBirth: 'Please enter a valid date of birth',
            age: 'Invalid date'
          }));
        } else if (age) {
          // Clear errors if valid
          setErrors(prev => ({
            ...prev,
            dateOfBirth: undefined,
            age: undefined
          }));
        }
      }
    }
    
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
    
    // Clear errors for updated fields (except dateOfBirth which is handled above)
    if (errors[field] && field !== 'dateOfBirth') {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Handle checkbox changes for multi-select fields
  const handleCheckboxChange = (field, value) => {
    setFormData(prev => {
      const currentValues = prev[field];
      let newValues;
      
      // Handle mutual exclusivity for radiation
      if (field === 'radiate') {
        if (value === 'does not radiate') {
          // If selecting "does not radiate", clear all others
          newValues = currentValues.includes(value) ? [] : [value];
        } else {
          // If selecting any other option, remove "does not radiate"
          newValues = currentValues.filter(v => v !== 'does not radiate');
          if (currentValues.includes(value)) {
            newValues = newValues.filter(v => v !== value);
          } else {
            newValues = [...newValues, value];
          }
        }
      }
      // Handle mutual exclusivity for risk factors
      else if (field === 'riskFactors') {
        if (value === 'no cardiac risk factors') {
          // If selecting "no cardiac risk factors", clear all others
          newValues = currentValues.includes(value) ? [] : [value];
        } else {
          // If selecting any other option, remove "no cardiac risk factors"
          newValues = currentValues.filter(v => v !== 'no cardiac risk factors');
          if (currentValues.includes(value)) {
            newValues = newValues.filter(v => v !== value);
          } else {
            newValues = [...newValues, value];
          }
        }
      }
      // Default behavior for other fields
      else {
        newValues = currentValues.includes(value)
          ? currentValues.filter(v => v !== value)
          : [...currentValues, value];
      }
      
      return {
        ...prev,
        [field]: newValues
      };
    });
    
    // Clear error when user selects something
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Validate current step
  const validateStep = (step) => {
    const stepErrors = {};
    
    switch(step) {
      case 0: // Screening question
        if (hasChestPain === null) stepErrors.hasChestPain = 'Please select an option';
        break;
      
      case 1: // Personal information
        if (!formData.fullName?.trim()) stepErrors.fullName = 'Required';
        if (!formData.age) stepErrors.age = 'Required';
        if (!formData.gender) stepErrors.gender = 'Required';
        if (!formData.dateOfBirth) {
          stepErrors.dateOfBirth = 'Required';
        } else {
          // Validate date is not in the future
          const today = new Date();
          const dob = new Date(formData.dateOfBirth);
          if (dob > today) {
            stepErrors.dateOfBirth = 'Date of birth cannot be in the future';
            stepErrors.age = 'Invalid date';
          } else {
            // Validate age is 18 to 120
            const age = parseInt(calculateAge(formData.dateOfBirth));
            if (age < 18) {
              stepErrors.dateOfBirth = 'You must be 18 years or older to use this service';
              stepErrors.age = 'Must be 18 or older';
            } else if (age > 120) {
              stepErrors.dateOfBirth = 'Please enter a valid date of birth';
              stepErrors.age = 'Invalid date';
            }
          }
        }
        if (!formData.email) {
          stepErrors.email = 'Required';
        } else {
          // Email validation regex
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(formData.email)) {
            stepErrors.email = 'Please enter a valid email address';
          }
        }
        break;
      
      case 2: // Pain character and radiation
        if (formData.characterPain.length === 0) stepErrors.characterPain = 'Select at least one';
        if (formData.radiate.length === 0) stepErrors.radiate = 'Select at least one';
        if (!formData.painBegin) stepErrors.painBegin = 'Required';
        if (!formData.frequency) stepErrors.frequency = 'Required';
        break;
      
      case 3: // Worsening factors
        if (formData.worsenedBy.length === 0) stepErrors.worsenedBy = 'Select at least one';
        break;
      
      case 4: // Relieving factors
        if (formData.relievedBy.length === 0) stepErrors.relievedBy = 'Select at least one';
        break;
      
      case 5: // Risk factors
        if (!formData.smoke) stepErrors.smoke = 'Required';
        if (formData.riskFactors.length === 0) stepErrors.riskFactors = 'Select at least one';
        break;
    }
    
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  // Navigate to next step
  // Allow free navigation without blocking on validation
  const handleNext = () => {
    // Run validation to populate errors but ignore result for navigation
    validateStep(currentStep);
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (currentStep > 0) showSaveMessage();
  };

  // Navigate to previous step
  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Submit form
  const handleSubmit = async () => {
    // Full form validation before final submit
    const { isValid, errors: allErrors } = validateFormData(formData);
    if (!isValid) {
      setErrors(allErrors);
      setSubmitError('Please complete all required fields before submitting.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await submitQuestionnaire(formData);
      console.log('Submission successful:', result);
      console.log('Looking for HTML content in:', result);
      
      // Extract HTML content from response if available
      // Try different possible locations in the response
      let htmlContent = null;
      
      if (result && result.html_content) {
        htmlContent = result.html_content;
        console.log('✅ Found HTML in result.html_content');
      } else if (result && result.result && result.result.html_content) {
        htmlContent = result.result.html_content;
        console.log('✅ Found HTML in result.result.html_content');
      } else if (result && result.body && result.body.html_content) {
        htmlContent = result.body.html_content;
        console.log('✅ Found HTML in result.body.html_content');
      } else {
        console.log('❌ No HTML content found in response. Response structure:', Object.keys(result || {}));
      }
      
      if (htmlContent) {
        setHtmlReport(htmlContent);
      }
      
      localStorage.removeItem('echestpain_progress'); // Clear saved progress
      setSubmitSuccess(true);
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError(error.message || 'Failed to submit questionnaire');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Developer tools functions
  const handleFillTestData = () => {
    setFormData(mockFormData);
    console.log('✅ Form filled with test data:', mockFormData);
  };

  const handleClearForm = () => {
    setFormData({
      fullName: '',
      age: '',
      gender: '',
      dateOfBirth: '',
      email: '',
      painBegin: '',
      frequency: '',
      characterPain: [],
      radiate: [],
      worsenedBy: [],
      relievedBy: [],
      smoke: '',
      riskFactors: []
    });
    setErrors({});
    console.log('🗑️ Form cleared');
  };

  const handleJumpToStep = (step) => {
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    console.log(`⏭️ Jumped to step ${step}`);
  };

  // Clear saved progress
  const handleClearProgress = () => {
    localStorage.removeItem('echestpain_progress');
    window.location.reload();
  };

  // Show save notification
  const showSaveMessage = () => {
    setShowSaveNotification(true);
    setTimeout(() => setShowSaveNotification(false), 3000);
  };

  // Render success screen
  if (submitSuccess) {
    return (
      <div className="questionnaire-container">
        <div className="questionnaire-header">
          <h1>eChestPain Assessment</h1>
        </div>
        <div className="form-card">
          {!showHtmlPreview ? (
            <div className="success-container">
              <div className="success-icon">✓</div>
              <h2>Thank You!</h2>
              <p className="mt-2">
                Your questionnaire has been submitted successfully.
              </p>
              <p className="mt-2">
                You will receive an email with your assessment report shortly at:
                <br />
                <strong>{formData.email}</strong>
              </p>
              <p className="mt-3" style={{ fontSize: '0.875rem', color: '#666' }}>
                Please check your spam folder if you don't see the email within 10 minutes.
              </p>
              
              {htmlReport && (
                <button
                  onClick={() => setShowHtmlPreview(true)}
                  style={{
                    marginTop: '2rem',
                    padding: '0.75rem 1.5rem',
                    background: '#4299e1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(66, 153, 225, 0.3)',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#3182ce'}
                  onMouseOut={(e) => e.target.style.background = '#4299e1'}
                >
                  📄 Preview Your Report
                </button>
              )}
            </div>
          ) : (
            <div style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: '#f7fafc',
              zIndex: 9999,
              overflow: 'auto',
              padding: '0.5rem'
            }}>
              <div style={{ 
                maxWidth: '1200px',
                margin: '0 auto'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: 'white',
                  borderRadius: '6px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  position: 'sticky',
                  top: '0.5rem',
                  zIndex: 10
                }}>
                  <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#2d3748' }}>
                    📄 Report Preview • {formData.email}
                  </span>
                  <button
                    onClick={() => setShowHtmlPreview(false)}
                    style={{
                      padding: '0.4rem 1rem',
                      background: '#4299e1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.background = '#3182ce'}
                    onMouseOut={(e) => e.target.style.background = '#4299e1'}
                  >
                    ✕ Close
                  </button>
                </div>
                
                <div style={{ 
                  background: 'white',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  minHeight: 'calc(100vh - 80px)'
                }}>
                  <iframe
                    srcDoc={htmlReport}
                    style={{
                      width: '100%',
                      minHeight: 'calc(100vh - 80px)',
                      border: 'none',
                      display: 'block'
                    }}
                    title="Assessment Report Preview"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ 
          textAlign: 'center', 
          padding: '2rem 1rem', 
          color: '#666',
          fontSize: '0.875rem',
          borderTop: '1px solid #e2e8f0',
          background: '#f7fafc'
        }}>
          <p style={{ marginBottom: '0.5rem', fontWeight: '600', color: '#4a5568' }}>
            eChestPain Symptom Checker System
          </p>
          <p style={{ marginBottom: '0.5rem' }}>
            © {new Date().getFullYear()} TheReferralsApp team
          </p>
          <p style={{ margin: 0 }}>
            <a href="https://thereferrals.app/" target="_blank" rel="noopener noreferrer" style={{ color: '#005EB8', textDecoration: 'none', marginRight: '1rem' }}>Website</a>
            <span style={{ marginRight: '1rem' }}>•</span>
            <a href="https://thereferrals.app/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#005EB8', textDecoration: 'none', marginRight: '1rem' }}>Privacy Policy</a>
            <span style={{ marginRight: '1rem' }}>•</span>
            <a href="https://thereferrals.app/contact" target="_blank" rel="noopener noreferrer" style={{ color: '#005EB8', textDecoration: 'none' }}>Contact</a>
          </p>
        </div>
      </div>
    );
  }

  // Positional progress (where the user is) exclude screening
  const progressPercentage = currentStep === 0 ? 0 : (currentStep / totalSteps) * 100;

  return (
    <div className="questionnaire-container">
      {/* Developer Tools (only visible in development) */}
      <DevTools
        onFillForm={handleFillTestData}
        onClearForm={handleClearForm}
        currentStep={currentStep}
        onJumpToStep={handleJumpToStep}
      />

      {/* Header */}
      <div className="questionnaire-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <img 
            src="https://html-email-files-threferralsapp.s3.amazonaws.com/email_template_files/image003.png" 
            alt="TheReferralsApp Logo" 
            style={{ height: '60px', borderRadius: '8px' }}
          />
          <div style={{ textAlign: 'right', fontSize: '0.875rem', opacity: 0.9 }}>
            <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>TheReferralsApp team</div>
            <div>
              <a href="https://thereferrals.app/" target="_blank" rel="noopener noreferrer" style={{ color: 'white', textDecoration: 'none' }}>
                https://thereferrals.app/
              </a>
            </div>
          </div>
        </div>
        <h1>eChestPain Assessment</h1>
        <p>Chest Pain Evaluation Questionnaire</p>
        {currentStep === 0 && (
          <p style={{ fontSize: '0.875rem', marginTop: '0.5rem', opacity: 0.9 }}>
            ⏱️ Estimated time: 5-7 minutes
          </p>
        )}
      </div>

      {/* Save notification */}
      {showSaveNotification && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#48bb78',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 10000,
          animation: 'slideIn 0.3s ease-out'
        }}>
          ✓ Progress saved automatically
        </div>
      )}

      {/* Clear progress button (only show if there's saved data and not on success screen) */}
      {!submitSuccess && currentStep > 0 && localStorage.getItem('echestpain_progress') && (
        <button
          onClick={handleClearProgress}
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            background: '#e53e3e',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.875rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 1000
          }}
          title="Clear all saved progress and start over"
        >
          🗑️ Clear Progress
        </button>
      )}

      {/* Anchor navigation */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'0.5rem', marginBottom:'1rem' }}>
        {[0,1,2,3,4,5,6].map(step => {
          const missing = step >=1 && step <=5 ? getStepErrors(step).length > 0 : false;
          return (
            <button
              key={step}
              type="button"
              onClick={() => handleJumpToStep(step)}
              style={{
                background: step === currentStep ? '#2b6cb0' : '#edf2f7',
                color: step === currentStep ? 'white' : '#2d3748',
                border:'1px solid ' + (step === currentStep ? '#2b6cb0' : '#cbd5e0'),
                padding:'0.4rem 0.65rem',
                borderRadius:'6px',
                fontSize:'0.75rem',
                cursor:'pointer',
                position:'relative'
              }}
            >
              {STEP_TITLES[step]}
              {step >=1 && step <=5 && (
                <span style={{
                  display:'inline-block',
                  marginLeft:'0.4rem',
                  width:'8px', height:'8px',
                  borderRadius:'50%',
                  background: missing ? '#e53e3e' : '#38a169'
                }} />
              )}
            </button>
          );
        })}
      </div>

      {/* Dual progress bars after screening */}
      {currentStep > 0 && (
        <div style={{ display:'grid', gap:'0.75rem', marginBottom:'1.5rem' }}>
          <div className="progress-container">
            <div className="progress-bar" style={{ marginBottom:'0.25rem' }}>
              <div className="progress-fill" style={{ width:`${progressPercentage}%`, background:'#3182ce' }} />
            </div>
            <div className="progress-text">Position: Step {currentStep} of {totalSteps}</div>
          </div>
          <div className="progress-container">
            <div className="progress-bar" style={{ marginBottom:'0.25rem' }}>
              <div className="progress-fill" style={{ width:`${overallCompletionPct}%`, background:'#38a169' }} />
            </div>
            <div className="progress-text">Completion: {completedCount} of 5 core steps complete</div>
          </div>
        </div>
      )}

      {/* Form content */}
      <div className="form-card">
        {submitError && (
          <div className="alert alert-error">
            <strong>Error:</strong> {submitError}
          </div>
        )}

        {/* Step 0: Screening Question */}
        {currentStep === 0 && (
          <div>
            <h2 className="mb-3">Initial Assessment</h2>
            
            <div className="question-group">
              <label className="question-label">
                Do you experience chest discomfort? <span className="required">*</span>
              </label>
              <div className="options-grid">
                <label 
                  className={`option-label ${hasChestPain === true ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="hasChestPain"
                    value="yes"
                    checked={hasChestPain === true}
                    onChange={() => {
                      setHasChestPain(true);
                      if (errors.hasChestPain) {
                        setErrors(prev => ({ ...prev, hasChestPain: undefined }));
                      }
                    }}
                  />
                  Yes
                </label>
                <label 
                  className={`option-label ${hasChestPain === false ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="hasChestPain"
                    value="no"
                    checked={hasChestPain === false}
                    onChange={() => {
                      setHasChestPain(false);
                      if (errors.hasChestPain) {
                        setErrors(prev => ({ ...prev, hasChestPain: undefined }));
                      }
                    }}
                  />
                  No
                </label>
              </div>
              {errors.hasChestPain && <div className="error-message">{errors.hasChestPain}</div>}
            </div>

            {hasChestPain === false && (
              <div className="alert alert-info" style={{ marginTop: '2rem' }}>
                <strong>You selected no chest discomfort.</strong>
                <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                  This e-consultation is not designed for your problems. We recommend seeking advice from a professional, the NHS website or 111.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 1: Personal Information */}
        {currentStep === 1 && (
          <div>
            <h2 className="mb-3">Personal Information {renderBadge(1)}</h2>
            
            <div className="question-group">
              <label className="question-label">
                Full Name <span className="required">*</span>
              </label>
              <input
                type="text"
                className={`input-field ${errors.fullName ? 'error' : ''}`}
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                placeholder="Enter your full name"
              />
              {errors.fullName && <div className="error-message">{errors.fullName}</div>}
            </div>

            <div className="question-group">
              <label className="question-label">
                Gender <span className="required">*</span>
              </label>
              <div className="options-grid">
                {FORM_FIELDS.GENDER_OPTIONS.map(option => (
                  <label 
                    key={option}
                    className={`option-label ${formData.gender === option ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="gender"
                      value={option}
                      checked={formData.gender === option}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                    />
                    {formatDisplayText(option)}
                  </label>
                ))}
              </div>
              {errors.gender && <div className="error-message">{errors.gender}</div>}
            </div>

            <div className="question-group">
              <label className="question-label">
                Date of Birth <span className="required">*</span>
              </label>
              <input
                type="date"
                className={`input-field ${errors.dateOfBirth ? 'error' : ''}`}
                value={formData.dateOfBirth}
                onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
              {errors.dateOfBirth && <div className="error-message">{errors.dateOfBirth}</div>}
            </div>

            <div className="question-group">
              <label className="question-label">
                Age <span className="required">*</span>
              </label>
              <span className="question-hint">
                Automatically calculated from your date of birth
              </span>
              <input
                type="number"
                className={`input-field ${errors.age ? 'error' : ''}`}
                value={formData.age}
                onChange={(e) => handleInputChange('age', e.target.value)}
                placeholder="Calculated from DOB"
                min="18"
                max="120"
                readOnly
                style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
              />
              {errors.age && <div className="error-message">{errors.age}</div>}
              {formData.age && formData.age < 18 && (
                <div className="alert alert-error" style={{ marginTop: '0.75rem' }}>
                  <strong>Age Restriction:</strong> This service is only available for patients aged 18 and over. Please seek advice from your GP, NHS 111, or attend A&E if urgent.
                </div>
              )}
            </div>

            <div className="question-group">
              <label className="question-label">
                Email Address <span className="required">*</span>
              </label>
              <span className="question-hint">
                Your assessment report will be sent to this email
              </span>
              <input
                type="email"
                className={`input-field ${errors.email ? 'error' : ''}`}
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="your.email@example.com"
              />
              {errors.email && <div className="error-message">{errors.email}</div>}
            </div>
          </div>
        )}

        {/* Step 2: Pain Characteristics */}
        {currentStep === 2 && (
          <div>
            <h2 className="mb-3">Pain Characteristics {renderBadge(2)}</h2>

            <div className="question-group">
              <label className="question-label">
                When did your chest pain first begin? <span className="required">*</span>
              </label>
              <input
                type="date"
                className={`input-field ${errors.painBegin ? 'error' : ''}`}
                value={formData.painBegin}
                onChange={(e) => handleInputChange('painBegin', e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
              {errors.painBegin && <div className="error-message">{errors.painBegin}</div>}
            </div>

            <div className="question-group">
              <label className="question-label">
                How often do you experience this pain? <span className="required">*</span>
              </label>
              <div className="options-grid">
                {FORM_FIELDS.FREQUENCY_OPTIONS.map(option => (
                  <label 
                    key={option}
                    className={`option-label ${formData.frequency === option ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="frequency"
                      value={option}
                      checked={formData.frequency === option}
                      onChange={(e) => handleInputChange('frequency', e.target.value)}
                    />
                    {formatDisplayText(option)}
                  </label>
                ))}
              </div>
              {errors.frequency && <div className="error-message">{errors.frequency}</div>}
            </div>
            
            <div className="question-group">
              <label className="question-label">
                What is the character of your pain? <span className="required">*</span>
              </label>
              <span className="question-hint">
                Choose one or more options that describe your pain
              </span>
              <div className="options-grid">
                {FORM_FIELDS.CHARACTER_PAIN_OPTIONS.map(option => (
                  <label 
                    key={option}
                    className={`option-label ${formData.characterPain.includes(option) ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.characterPain.includes(option)}
                      onChange={() => handleCheckboxChange('characterPain', option)}
                    />
                    {formatDisplayText(option)}
                  </label>
                ))}
              </div>
              {formData.characterPain.length > 4 && (
                <div className="alert alert-info" style={{ marginTop: '1rem' }}>
                  <strong>Note:</strong> You have selected {formData.characterPain.length} pain characteristics. Please focus on the episodes with the most severe symptoms and try to be specific about the pain. This will help the clinician understand the nature of the pain.
                </div>
              )}
              {errors.characterPain && <div className="error-message">{errors.characterPain}</div>}
            </div>

            <div className="question-group">
              <label className="question-label">
                Does your chest pain radiate to other parts of your body? <span className="required">*</span>
              </label>
              <span className="question-hint">
                If pain does not radiate, select "does not radiate" only
              </span>
              <div className="options-grid">
                {FORM_FIELDS.RADIATE_OPTIONS.map(option => (
                  <label 
                    key={option}
                    className={`option-label ${formData.radiate.includes(option) ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.radiate.includes(option)}
                      onChange={() => handleCheckboxChange('radiate', option)}
                      disabled={
                        (option === 'does not radiate' && formData.radiate.length > 0 && !formData.radiate.includes('does not radiate')) ||
                        (option !== 'does not radiate' && formData.radiate.includes('does not radiate'))
                      }
                    />
                    {formatDisplayText(option)}
                  </label>
                ))}
              </div>
              {errors.radiate && <div className="error-message">{errors.radiate}</div>}
            </div>
          </div>
        )}

        {/* Step 3: Worsening Factors */}
        {currentStep === 3 && (
          <div>
            <h2 className="mb-3">What Makes It Worse? {renderBadge(3)}</h2>
            
            <div className="question-group">
              <label className="question-label">
                Is your chest pain worsened by...? <span className="required">*</span>
              </label>
              <span className="question-hint">
                Choose one or more options
              </span>
              <div className="options-grid">
                {FORM_FIELDS.WORSENED_BY_OPTIONS.map(option => (
                  <label 
                    key={option}
                    className={`option-label ${formData.worsenedBy.includes(option) ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.worsenedBy.includes(option)}
                      onChange={() => handleCheckboxChange('worsenedBy', option)}
                    />
                    {formatDisplayText(option)}
                  </label>
                ))}
              </div>
              {formData.worsenedBy.length > 4 && (
                <div className="alert alert-info" style={{ marginTop: '1rem' }}>
                  <strong>Note:</strong> You have selected {formData.worsenedBy.length} factors. Please focus on the most consistent triggers and try to be specific. This will help the clinician understand your pain pattern.
                </div>
              )}
              {errors.worsenedBy && <div className="error-message">{errors.worsenedBy}</div>}
            </div>
          </div>
        )}

        {/* Step 4: Relieving Factors */}
        {currentStep === 4 && (
          <div>
            <h2 className="mb-3">What Makes It Better? {renderBadge(4)}</h2>
            
            <div className="question-group">
              <label className="question-label">
                Is your chest pain relieved by...? <span className="required">*</span>
              </label>
              <span className="question-hint">
                Choose one or more options
              </span>
              <div className="options-grid">
                {FORM_FIELDS.RELIEVED_BY_OPTIONS.map(option => (
                  <label 
                    key={option}
                    className={`option-label ${formData.relievedBy.includes(option) ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.relievedBy.includes(option)}
                      onChange={() => handleCheckboxChange('relievedBy', option)}
                    />
                    {option === 'glyceryl trinitrate (GTN) spray' ? (
                      <Tooltip text="Nitroglycerin - a spray used to relieve angina">
                        {formatDisplayText(option)}
                      </Tooltip>
                    ) : option === 'antacids (Gaviscon, milk of magnesia etc)' ? (
                      <Tooltip text="Medicines that neutralise stomach acid">
                        {formatDisplayText(option)}
                      </Tooltip>
                    ) : formatDisplayText(option)}
                  </label>
                ))}
              </div>
              {formData.relievedBy.length > 4 && (
                <div className="alert alert-info" style={{ marginTop: '1rem' }}>
                  <strong>Note:</strong> You have selected {formData.relievedBy.length} factors. Please focus on the most effective relief methods and try to be specific. This will help the clinician understand your pain pattern.
                </div>
              )}
              {errors.relievedBy && <div className="error-message">{errors.relievedBy}</div>}
            </div>
          </div>
        )}

        {/* Step 5: Risk Factors */}
        {currentStep === 5 && (
          <div>
            <h2 className="mb-3">Medical History & Risk Factors {renderBadge(5)}</h2>
            
            <div className="question-group">
              <label className="question-label">
                Do you smoke? <span className="required">*</span>
              </label>
              <div className="options-grid">
                {FORM_FIELDS.SMOKE_OPTIONS.map(option => (
                  <label 
                    key={option}
                    className={`option-label ${formData.smoke === option ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="smoke"
                      value={option}
                      checked={formData.smoke === option}
                      onChange={(e) => handleInputChange('smoke', e.target.value)}
                    />
                    {formatDisplayText(option)}
                  </label>
                ))}
              </div>
              {errors.smoke && <div className="error-message">{errors.smoke}</div>}
            </div>

            <div className="question-group">
              <label className="question-label">
                Do you have any of the following conditions? <span className="required">*</span>
              </label>
              <span className="question-hint">
                Choose all that apply, or select "no cardiac risk factors" if you have no risk factors
              </span>
              <div className="options-grid">
                {FORM_FIELDS.RISK_FACTORS_OPTIONS.map(option => (
                  <label 
                    key={option}
                    className={`option-label ${formData.riskFactors.includes(option) ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.riskFactors.includes(option)}
                      onChange={() => handleCheckboxChange('riskFactors', option)}
                      disabled={
                        (option === 'no cardiac risk factors' && formData.riskFactors.length > 0 && !formData.riskFactors.includes('no cardiac risk factors')) ||
                        (option !== 'no cardiac risk factors' && formData.riskFactors.includes('no cardiac risk factors'))
                      }
                    />
                    {option === 'kidney disease (ckd)' ? (
                      <Tooltip text="Chronic Kidney Disease - long-term kidney damage">
                        {formatDisplayText(option)}
                      </Tooltip>
                    ) : option === 'coronary artery bypass grafts (cabg)' ? (
                      <Tooltip text="Heart bypass surgery - surgical procedure to improve blood flow to the heart">
                        {formatDisplayText(option)}
                      </Tooltip>
                    ) : option === 'cardiac stents (pci)' ? (
                      <Tooltip text="Percutaneous Coronary Intervention - procedure to open blocked heart arteries">
                        {formatDisplayText(option)}
                      </Tooltip>
                    ) : formatDisplayText(option)}
                  </label>
                ))}
              </div>
              {errors.riskFactors && <div className="error-message">{errors.riskFactors}</div>}
            </div>
          </div>
        )}

        {/* Step 6: Summary */}
        {currentStep === 6 && (
          <div>
            <h2 className="mb-3">Review Your Answers</h2>
            <p style={{ marginBottom: '2rem', color: '#666' }}>
              Please review your answers before submitting. You can go back to edit any information.
            </p>

            {/* Missing fields summary */}
            {(() => {
              const allMissing = [1,2,3,4,5].flatMap(step => getStepErrors(step));
              if (allMissing.length === 0) return null;
              return (
                <div style={{ background:'#fff5f5', border:'1px solid #fc8181', color:'#742a2a', padding:'1rem 1.25rem', borderRadius:'8px', marginBottom:'1.5rem' }}>
                  <strong>Incomplete Sections:</strong>
                  <ul style={{ margin:'0.5rem 0 0 1rem', padding:0, fontSize:'0.875rem' }}>
                    {[...new Set(allMissing)].map(f => (
                      <li key={f}>{FIELD_LABELS[f] || f}</li>
                    ))}
                  </ul>
                  <div style={{ marginTop:'0.75rem', fontSize:'0.75rem' }}>
                    Navigate to the highlighted steps to complete these before submitting.
                  </div>
                </div>
              );
            })()}

            <div style={{ background: '#f8f9fa', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#005EB8' }}>Personal Information</h3>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div><strong>Full Name:</strong> {formData.fullName}</div>
                <div><strong>Gender:</strong> {formData.gender}</div>
                <div><strong>Date of Birth:</strong> {formData.dateOfBirth}</div>
                <div><strong>Age:</strong> {formData.age}</div>
                <div><strong>Email:</strong> {formData.email}</div>
              </div>
            </div>

            <div style={{ background: '#f8f9fa', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#005EB8' }}>Pain Information</h3>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div><strong>Pain began:</strong> {formData.painBegin}</div>
                <div><strong>Frequency:</strong> {formatDisplayText(formData.frequency)}</div>
                <div><strong>Character of pain:</strong> {formData.characterPain.map(p => formatDisplayText(p)).join(', ')}</div>
                <div><strong>Radiation:</strong> {formData.radiate.map(r => formatDisplayText(r)).join(', ')}</div>
              </div>
            </div>

            <div style={{ background: '#f8f9fa', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#005EB8' }}>Pain Pattern</h3>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div><strong>Worsened by:</strong> {formData.worsenedBy.map(w => formatDisplayText(w)).join(', ')}</div>
                <div><strong>Relieved by:</strong> {formData.relievedBy.map(r => formatDisplayText(r)).join(', ')}</div>
              </div>
            </div>

            <div style={{ background: '#f8f9fa', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#005EB8' }}>Medical History</h3>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div><strong>Smoking status:</strong> {formData.smoke}</div>
                <div><strong>Risk factors:</strong> {formData.riskFactors.map(r => formatDisplayText(r)).join(', ')}</div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="button-group">
          {currentStep > 0 && (
            <button 
              className="btn btn-secondary"
              onClick={handlePrevious}
              disabled={isSubmitting}
            >
              ← Previous
            </button>
          )}
          
          <div style={{ flex: 1 }} />
          
          {currentStep === 0 ? (
            hasChestPain === true && (
              <button 
                className="btn btn-primary"
                onClick={handleNext}
              >
                Continue →
              </button>
            )
          ) : currentStep < 5 ? (
            <button 
              className="btn btn-primary"
              onClick={handleNext}
            >
              Next →
            </button>
          ) : currentStep === 5 ? (
            <button 
              className="btn btn-primary"
              onClick={handleNext}
            >
              Review Answers →
            </button>
          ) : (
            <button 
              className="btn btn-success"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner" style={{ width: '20px', height: '20px', borderWidth: '2px' }} />
                  Submitting...
                </>
              ) : (
                'Submit Assessment'
              )}
            </button>
          )}
        </div>
      </div>

      {/* Footer */}
      {currentStep > 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '2rem 1rem', 
          color: '#666',
          fontSize: '0.875rem',
          borderTop: '1px solid #e2e8f0',
          background: '#f7fafc'
        }}>
          <p style={{ marginBottom: '0.5rem', fontWeight: '600', color: '#4a5568' }}>
            eChestPain Symptom Checker System
          </p>
          <p style={{ marginBottom: '0.5rem' }}>
            © {new Date().getFullYear()} TheReferralsApp team
          </p>
          <p style={{ margin: 0 }}>
            <a href="https://thereferrals.app/" target="_blank" rel="noopener noreferrer" style={{ color: '#005EB8', textDecoration: 'none', marginRight: '1rem' }}>Website</a>
            <span style={{ marginRight: '1rem' }}>•</span>
            <a href="https://thereferrals.app/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#005EB8', textDecoration: 'none', marginRight: '1rem' }}>Privacy Policy</a>
            <span style={{ marginRight: '1rem' }}>•</span>
            <a href="https://thereferrals.app/contact" target="_blank" rel="noopener noreferrer" style={{ color: '#005EB8', textDecoration: 'none' }}>Contact</a>
          </p>
        </div>
      )}
    </div>
  );
};

export default Questionnaire;

